/**
 * Tauri 桌面端键盘快捷键 Hook
 * 支持 Cmd/Ctrl+S 保存、Cmd/Ctrl+O 打开等
 * 同时监听文件关联打开事件
 */
import { useEffect, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '@/store/useAppStore'
import { isTauri, writeMarkdownFile, saveFileDialog, openAndReadFile } from '@/services/tauriService'

/**
 * 从 Markdown 内容中提取标题
 */
const extractTitleFromMarkdown = (content: string): string => {
  if (!content) return ''
  const titleMatch = content.match(/^#\s+(.+)$/m)
  if (titleMatch && titleMatch[1]) {
    return titleMatch[1].trim()
  }
  return ''
}

export function useTauriShortcuts() {
  const { t } = useTranslation()
  const { 
    currentDocument, 
    currentFilePath, 
    setCurrentFilePath,
    setCurrentDocument,
    saveDocument 
  } = useAppStore()

  // 保存当前文件到本地
  const saveToFile = useCallback(async () => {
    if (!isTauri() || !currentDocument) return

    try {
      let path = currentFilePath

      // 如果没有文件路径，弹出另存为对话框
      if (!path) {
        const fileName = currentDocument.title 
          ? `${currentDocument.title}.md` 
          : 'untitled.md'
        path = await saveFileDialog(fileName)
        if (!path) return // 用户取消
      }

      // 写入文件
      await writeMarkdownFile(path, currentDocument.content)
      setCurrentFilePath(path)
      toast.success(t('export.success'))
    } catch (error) {
      console.error('Failed to save file:', error)
      toast.error(t('export.failed'))
    }
  }, [currentDocument, currentFilePath, setCurrentFilePath, t])

  // 打开本地文件
  const openFile = useCallback(async () => {
    if (!isTauri()) return

    try {
      const result = await openAndReadFile()
      if (result) {
        const title = extractTitleFromMarkdown(result.content) || 
          result.path.split('/').pop()?.replace(/\.(md|markdown)$/, '') || 
          'Untitled'

        setCurrentDocument(null)
        setCurrentFilePath(result.path)
        saveDocument(result.content, title)
        toast.success(t('import.success'))
      }
    } catch (error) {
      console.error('Failed to open file:', error)
      toast.error(t('import.failed'))
    }
  }, [setCurrentDocument, setCurrentFilePath, saveDocument, t])

  // 键盘快捷键处理
  useEffect(() => {
    if (!isTauri()) return

    const handleKeyDown = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey

      // Cmd/Ctrl + S: 保存
      if (isMod && e.key === 's') {
        e.preventDefault()
        saveToFile()
      }

      // Cmd/Ctrl + O: 打开
      if (isMod && e.key === 'o') {
        e.preventDefault()
        openFile()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [saveToFile, openFile])

  // 防止重复处理的标记
  const hasHandledFileOpen = useRef(false)
  const lastHandledPath = useRef<string | null>(null)

  // 处理打开文件的通用逻辑
  const handleFileOpen = useCallback((path: string, content: string) => {
    // 防止重复处理同一个文件
    if (lastHandledPath.current === path) {
      return
    }
    
    lastHandledPath.current = path
    
    const title = extractTitleFromMarkdown(content) || 
      path.split('/').pop()?.replace(/\.(md|markdown)$/, '') || 
      'Untitled'

    // 直接创建新文档并设置为当前文档
    const now = Date.now()
    const newDoc = {
      id: now,
      title,
      content,
      created: now,
      updated: now
    }
    
    // 使用 store 的原子操作
    useAppStore.setState((state) => ({
      currentDocument: newDoc,
      documents: [...state.documents, newDoc],
      currentFilePath: path
    }))
  }, [])

  // 监听文件关联打开事件（右键用 MarkFlow 打开 .md 文件）
  useEffect(() => {
    if (!isTauri()) return

    let unlisten: (() => void) | undefined
    let pollInterval: ReturnType<typeof setInterval> | undefined

    const checkPendingFile = async () => {
      try {
        const { invoke } = await import('@tauri-apps/api/core')
        const pendingFile = await invoke<{ path: string } | null>('get_pending_file')
        if (pendingFile) {
          const content = await invoke<string>('read_markdown_file', { path: pendingFile.path })
          handleFileOpen(pendingFile.path, content)
          hasHandledFileOpen.current = true
          await invoke('clear_pending_file')
        }
      } catch (e) {
        // Silently fail - not in Tauri environment
      }
    }

    const setupListener = async () => {
      try {
        const { listen } = await import('@tauri-apps/api/event')
        
        // 立即检查待打开的文件
        await checkPendingFile()
        
        // 如果还没处理到文件，每隔 500ms 检查一次，最多检查 5 次
        if (!hasHandledFileOpen.current) {
          let attempts = 0
          pollInterval = setInterval(async () => {
            attempts++
            await checkPendingFile()
            if (hasHandledFileOpen.current || attempts >= 5) {
              clearInterval(pollInterval)
            }
          }, 500)
        }
        
        // 监听 file-open 事件（用于应用已运行时接收新文件）
        unlisten = await listen<{ path: string }>('file-open', async (event) => {
          try {
            const { invoke } = await import('@tauri-apps/api/core')
            const content = await invoke<string>('read_markdown_file', { path: event.payload.path })
            handleFileOpen(event.payload.path, content)
          } catch {
            // Silently fail
          }
        })
      } catch {
        // Not in Tauri environment
      }
    }

    setupListener()

    return () => {
      if (unlisten) unlisten()
      if (pollInterval) clearInterval(pollInterval)
    }
  }, [handleFileOpen])

  return {
    saveToFile,
    openFile,
    isDesktop: isTauri(),
    currentFilePath
  }
}

export default useTauriShortcuts
